import * as Sentry from "@sentry/react";

import { activateStep, clearActiveStep } from "Store/steps/stepsSlice";
import { Form, FormikProps } from "formik";
import {
  CarProductIntroFormValues,
  VehicleInputType,
} from "./FormElements/interfaces";

import { StyledAlert } from "App/global-style";
import { MarketingConsent } from "Components/Consent/MarketingConsent";
import { ProductIntroTile } from "Components/Features/CheckPrice/Tiles/ProductIntroTile";
import { PartnerProductIntroForms } from "Components/Features/CheckPrice/Tiles/ProductIntroTile/PartnerProductIntroForms/PartnerProductIntroForms";
import { AlertType } from "Components/Form/Alert";
import { NavigationButtons } from "Components/NavigationButtons";
import { ScrollToFirstError } from "Components/ScrollToFirstError";
import { useTexts } from "Components/TextContext";
import { initialFormAboutValues } from "Features/CheckPrice/Products/RiskProducts/VehicleProducts/Car/ProductDetails/CarProductDetails";
import { initialPriceFormState as originalInitialPriceFormState } from "Features/CheckPrice/Products/RiskProducts/VehicleProducts/Car/ProductPrice/CarProductPrice";
import { CarPriceFormValues } from "Features/CheckPrice/Products/RiskProducts/VehicleProducts/Car/ProductPrice/interfaces";
import { mapFormToPriceRequest } from "Features/CheckPrice/Products/RiskProducts/VehicleProducts/Car/utils/mapFormToPriceRequest";
import { ProductBackendName } from "Features/CheckPrice/Products/productTypes";
import { useUserIdentification } from "Features/CheckPrice/userIdentificationHook";
import { useFeatureToggle } from "Store/featureToggles/featureToggleHooks";
import { FeatureToggle } from "Store/featureToggles/featureToggleTypes";
import { INSURANCE_NAME_VALUE } from "Store/interfaces/products";
import { CarPriceRequestInfo } from "Store/interfaces/products/checkPrice/riskProducts/VehicleProducts/Car";
import { useFormState } from "Store/priceCalculation/priceCalculationHooks";
import { updatePriceCalculationFormState } from "Store/priceCalculation/priceCalculationSlice";
import { ReduxState } from "Store/rootReducer";
import { isAuthenticated } from "Utils/KeycloakWrapper";
import { isDefined } from "Utils/functions";
import { useUnleashFeatureFlag } from "Utils/unleash";
import { FeatureFlagTask } from "Utils/unleash/flags";
import React from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { Reason } from "../InvalidInsuranceObject";
import { INVALID_OBJECT } from "../carConstants";
import { CAR_ABOUT_STEP, CAR_FIND_STEP, CAR_PRICE_STEP } from "../carSteps";
import { FormElements } from "./FormElements";
import { FormElements as FormElementsOld } from "./FormElementsOld";
import { validationSchema } from "./carProductIntroValidation";

type Props = {
  updatePriceRequest: (
    payload: CarPriceRequestInfo,
    stepForwardOnSuccess: boolean,
  ) => void;
};
export const CarProductIntro = ({
  updatePriceRequest,
}: Props): React.ReactElement => {
  const dispatch = useDispatch();
  const initialFormState = useFormState(CAR_FIND_STEP.id, {
    licensePlateNumberSearch: "",
    licensePlateNumber: "",
    licensePlateNumberSelected: "",
    toggleVehicleInputType: true,
    vehicleInputType: isAuthenticated()
      ? VehicleInputType.OWNED
      : VehicleInputType.LICENSE_PLATE,
  });

  const { getText } = useTexts();
  const history = useHistory();
  const userIdentification = useUserIdentification(ProductBackendName.CAR);
  const blockingPage = useFeatureToggle(FeatureToggle.BLOCKING_PAGE);
  const isNewFindCarFlowEnabled = useUnleashFeatureFlag(
    FeatureFlagTask.SSX_1955_NEW_FLOW_FIND_CAR,
  );

  const { current: vehicle } = useSelector(
    (state: ReduxState) => state.findVehicle,
  );
  const foundVehicleWithMissingConfigId =
    isDefined(vehicle) && !isDefined(vehicle.configurationId);

  const handleSubmit = (formValues: CarProductIntroFormValues): void => {
    if (formValues.canBeBoughtOnline === false && blockingPage) {
      dispatch(clearActiveStep());
      return history.push(INVALID_OBJECT, { reason: Reason.INVALID_VEHICLE });
    }

    const age = userIdentification.current?.newCustomerResponse.age
      ? Number(userIdentification.current?.newCustomerResponse.age)
      : 0;

    dispatch(
      updatePriceCalculationFormState({
        stepId: CAR_FIND_STEP.id,
        formState: formValues,
      }),
    );
    const initialAboutFormState = initialFormAboutValues(age);
    dispatch(
      updatePriceCalculationFormState({
        stepId: CAR_ABOUT_STEP.id,
        formState: { ...initialAboutFormState, automaticallyPopulated: true },
      }),
    );
    const initialPriceFormState =
      originalInitialPriceFormState as CarPriceFormValues;
    dispatch(
      updatePriceCalculationFormState({
        stepId: CAR_PRICE_STEP.id,
        formState: initialPriceFormState,
      }),
    );

    updatePriceRequest(
      mapFormToPriceRequest(
        userIdentification.current,
        formValues,
        initialAboutFormState,
        initialPriceFormState,
      ),
      false,
    );

    dispatch(activateStep(CAR_PRICE_STEP.id));
  };

  return (
    <Sentry.ErrorBoundary
      beforeCapture={(scope) => {
        scope.setTag(
          "location",
          `CheckPrice - ProductIntro - ${INSURANCE_NAME_VALUE.CAR}`,
        );
      }}
    >
      <ProductIntroTile
        productBackendName={ProductBackendName.CAR}
        validationSchema={validationSchema(getText)}
        initialValues={initialFormState}
        onSubmit={(values: CarProductIntroFormValues) => handleSubmit(values)}
      >
        {(formProps: FormikProps<CarProductIntroFormValues>) => {
          const isErrorAndCantFetchCarInfo =
            [VehicleInputType.OWNED, VehicleInputType.LICENSE_PLATE].includes(
              formProps.values.vehicleInputType,
            ) &&
            formProps.values.canBeBoughtOnline &&
            formProps.submitCount > 0 &&
            !formProps.isValid;

          return (
            <Form>
              {isNewFindCarFlowEnabled ? (
                <FormElements />
              ) : (
                <FormElementsOld formProps={formProps} />
              )}
              {isErrorAndCantFetchCarInfo && (
                <StyledAlert
                  type="error"
                  title={getText("carform.alertdescription.cantfetchcarinfo")}
                  alertType={AlertType.INPUT_ERROR_ALERT}
                  alertId="missingVehicleInformation"
                />
              )}

              <PartnerProductIntroForms isWithSurface />

              <ScrollToFirstError />

              {(formProps.values.vehicleInputType === VehicleInputType.MANUAL ||
                (VehicleInputType.LICENSE_PLATE &&
                  foundVehicleWithMissingConfigId)) && (
                <NavigationButtons
                  hidePreviousButton
                  disableNextButton={
                    (formProps.values.canBeBoughtOnline === false &&
                      !blockingPage) ||
                    formProps.values.isAlreadyInsured
                  }
                />
              )}
              <MarketingConsent />
            </Form>
          );
        }}
      </ProductIntroTile>
    </Sentry.ErrorBoundary>
  );
};
